import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { Task } from '../interfaces/Task';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import { useTranslation } from 'react-i18next';

interface SelectedItem {
    id: string;
    type: 'section' | 'task';
}

interface TaskIconProps {
    task: Task;
    onDoubleClick: () => void;
    onDelete: () => void;
    onDeleteSelected?: () => void;
    onDuplicate: () => void;
    onShare?: () => void;
    onShareLink?: () => void;
    viewMode?: 'icons' | 'list';
    isSelected?: boolean;
    onSelect?: (ctrlKey: boolean) => void;
    selectedItems?: SelectedItem[];
}

export default function TaskIcon(props: TaskIconProps) {
    const { t } = useTranslation();
    const {
        task,
        onDoubleClick,
        onDelete,
        onDeleteSelected,
        onDuplicate,
        onShare,
        onShareLink,
        viewMode = 'icons',
        isSelected = false,
        onSelect,
        selectedItems = []
    } = props;
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
    const hasMultiSelection = selectedItems.length > 1;

    const [{ isDragging }, drag] = useDrag(() => ({
        type: 'task',
        item: () => {
            // If this item is selected and there are multiple selections, drag all
            if (isSelected && selectedItems.length > 1) {
                return {
                    id: task._id,
                    taskId: task._id,
                    type: 'task' as const,
                    task: task,
                    selectedItems: selectedItems
                };
            }
            return {
                id: task._id,
                taskId: task._id,
                type: 'task' as const,
                task: task,
                selectedItems: [{ id: task._id, type: 'task' as const }]
            };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [task._id, isSelected, selectedItems]);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onSelect) {
            onSelect(e.ctrlKey || e.metaKey);
        }
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ x: e.clientX, y: e.clientY });
    };

    // Context menu items depend on whether we have multi-selection
    const contextMenuItems: ContextMenuItem[] = hasMultiSelection && isSelected
        ? [
            {
                label: `${t("Delete")} (${selectedItems.length})`,
                icon: 'bi-trash',
                onClick: onDeleteSelected || onDelete,
                danger: true
            }
        ]
        : [
            {
                label: t("Open"),
                icon: 'bi-pencil-square',
                onClick: onDoubleClick
            },
            ...(onShare ? [{
                label: t("Share"),
                icon: 'bi-share',
                onClick: onShare
            }] : []),
            ...(onShareLink ? [{
                label: t("Share link"),
                icon: 'bi-link-45deg',
                onClick: onShareLink
            }] : []),
            {
                label: t("Duplicate"),
                icon: 'bi-copy',
                onClick: onDuplicate
            },
            {
                label: t("Delete"),
                icon: 'bi-trash',
                onClick: onDelete,
                danger: true,
                divider: true
            }
        ];

    if (viewMode === 'list') {
        return (
            <>
                <div
                    ref={drag as any}
                    className={`list-item task-list-item ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={handleClick}
                    onDoubleClick={onDoubleClick}
                    onContextMenu={handleContextMenu}
                >
                    <div className="list-item-icon">
                        <i className="bi bi-file-earmark-text"></i>
                    </div>
                    <div className="list-item-name">{task.name}</div>
                    <div className="list-item-actions">
                        <button
                            className="list-action-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDoubleClick();
                            }}
                            title={t("Edit")}
                        >
                            <i className="bi bi-pencil"></i>
                        </button>
                        {onShare && (
                            <button
                                className="list-action-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShare();
                                }}
                                title={t("Share")}
                            >
                                <i className="bi bi-share"></i>
                            </button>
                        )}
                        {onShareLink && (
                            <button
                                className="list-action-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onShareLink();
                                }}
                                title={t("Share link")}
                            >
                                <i className="bi bi-link-45deg"></i>
                            </button>
                        )}
                        <button
                            className="list-action-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDuplicate();
                            }}
                            title={t("Duplicate")}
                        >
                            <i className="bi bi-copy"></i>
                        </button>
                        <button
                            className="list-action-btn delete-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            title={t("Delete")}
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
                {contextMenu && (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        items={contextMenuItems}
                        onClose={() => setContextMenu(null)}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div
                ref={drag as any}
                className={`icon-item task-icon ${isDragging ? 'dragging' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={handleClick}
                onDoubleClick={onDoubleClick}
                onContextMenu={handleContextMenu}
                title={task.name}
            >
                <div className="icon">
                    <i className="bi bi-file-earmark-text"></i>
                </div>
                <div className="name">{task.name}</div>
                <div className="icon-actions">
                    <button
                        className="icon-action-btn edit-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDoubleClick();
                        }}
                        title={t("Edit")}
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                    {onShare && (
                        <button
                            className="icon-action-btn share-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onShare();
                            }}
                            title={t("Share")}
                        >
                            <i className="bi bi-share"></i>
                        </button>
                    )}
                    {onShareLink && (
                        <button
                            className="icon-action-btn share-link-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onShareLink();
                            }}
                            title={t("Share link")}
                        >
                            <i className="bi bi-link-45deg"></i>
                        </button>
                    )}
                    <button
                        className="icon-action-btn duplicate-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDuplicate();
                        }}
                        title={t("Duplicate")}
                    >
                        <i className="bi bi-copy"></i>
                    </button>
                    <button
                        className="icon-action-btn delete-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        title={t("Delete")}
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            </div>
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenuItems}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </>
    );
}
